import { Fragment } from 'react';
import { Link } from 'wouter';
import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { isUnavailableBouquetCreatorPath } from '@/lib/storeFeatures';

interface GuideParagraph {
  readonly text: string;
  readonly links?: readonly { readonly text: string; readonly href: string }[];
}

interface GuideSection {
  readonly heading: string;
  readonly paragraphs: readonly GuideParagraph[];
  readonly subsections?: readonly {
    readonly heading: string;
    readonly paragraphs: readonly GuideParagraph[];
  }[];
}

interface BuyingGuide {
  readonly id: string;
  readonly eyebrow: string;
  readonly sections: readonly GuideSection[];
}

function Paragraph({ paragraph, fieldId }: { paragraph: GuideParagraph; fieldId: string }) {
  let cursor = 0;
  const parts: { text: string; href?: string }[] = [];
  for (const link of paragraph.links ?? []) {
    const position = paragraph.text.indexOf(link.text, cursor);
    if (position < 0) continue;
    if (position > cursor) parts.push({ text: paragraph.text.slice(cursor, position) });
    parts.push({ text: link.text, href: link.href });
    cursor = position + link.text.length;
  }
  if (cursor < paragraph.text.length) parts.push({ text: paragraph.text.slice(cursor) });

  return (
    <p style={{ margin: '0 0 18px', fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.2vw, 15px)', lineHeight: 1.85, color: '#5e4535' }}>
      {parts.map((part, index) => {
        const text = <CmsEditorialText id={`${fieldId}.${index}`} value={part.text} section="Poradnik wyboru" label={`Fragment akapitu ${index + 1}`} multiline={!part.href} />;
        return part.href && !isUnavailableBouquetCreatorPath(part.href)
          ? <Link key={index} href={part.href} style={{ color: '#9B2335', textDecoration: 'underline', textUnderlineOffset: 3 }}>{text}</Link>
          : <Fragment key={index}>{text}</Fragment>;
      })}
    </p>
  );
}

export function CategoryBuyingGuide({ content }: { content: BuyingGuide }) {
  const prefix = `category.guide.${content.id}`;
  return (
    <section aria-label="Poradnik wyboru kwiatów" data-testid={`buying-guide-${content.id}`} style={{ background: '#fff' }}>
      <div className="page-container" style={{ paddingBlock: 'clamp(32px, 5vw, 64px)', maxWidth: 1160 }}>
        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#9B2335', margin: '0 0 28px' }}>
          <CmsEditorialText id={`${prefix}.eyebrow`} value={content.eyebrow} section="Poradnik wyboru" label="Nadtytuł" />
        </p>
        {content.sections.map((section, index) => (
          <div key={section.heading} className="category-buying-guide-row" style={{ borderTop: '1px solid rgba(210,60,120,0.18)', paddingBlock: 'clamp(24px, 4vw, 40px)', display: 'grid', gap: 'clamp(16px, 3vw, 40px)' }}>
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(26px, 2.8vw, 34px)', fontWeight: 500, lineHeight: 1.2, color: '#2A1F14', margin: 0 }}>
              <CmsEditorialText id={`${prefix}.${index}.heading`} value={section.heading} section="Poradnik wyboru" label={`Nagłówek ${index + 1}`} />
            </h2>
            <div>
              {section.paragraphs.map((paragraph, paragraphIndex) => <Paragraph key={paragraphIndex} paragraph={paragraph} fieldId={`${prefix}.${index}.paragraph.${paragraphIndex}`} />)}
              {section.subsections?.map((subsection, subsectionIndex) => (
                <div key={subsection.heading}>
                  <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 16, fontWeight: 600, color: '#2A1F14', lineHeight: 1.5, margin: '22px 0 12px' }}>
                    <CmsEditorialText id={`${prefix}.${index}.subsection.${subsectionIndex}.heading`} value={subsection.heading} section="Poradnik wyboru" label={`Podtytuł ${index + 1}.${subsectionIndex + 1}`} />
                  </h3>
                  {subsection.paragraphs.map((paragraph, paragraphIndex) => <Paragraph key={paragraphIndex} paragraph={paragraph} fieldId={`${prefix}.${index}.subsection.${subsectionIndex}.paragraph.${paragraphIndex}`} />)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <style>{`
        .category-buying-guide-row { grid-template-columns: minmax(0, 1fr); }
        @media (min-width: 900px) { .category-buying-guide-row { grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.7fr); } }
      `}</style>
    </section>
  );
}
